import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Eye, Trash2, Save, X } from 'lucide-react';
import WYSIWYGEditor from '../components/WYSIWYGEditor';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminEmailTemplates = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    template_type: 'general'
  });

  const templateTypes = [
    { value: 'general', label: 'Generell' },
    { value: 'user_welcome', label: 'Velkomst-e-post' },
    { value: 'password_reset', label: 'Passord tilbakestilling' },
    { value: 'subscription_expired', label: 'Abonnement utløpt' },
    { value: 'subscription_reminder', label: 'Abonnement påminnelse' },
    { value: 'payment_success', label: 'Betaling vellykket' },
    { value: 'invoice', label: 'Faktura' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Kunne ikke laste e-postmaler');
      console.error('Fetch templates error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: '',
      template_type: 'general'
    });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      template_type: template.template_type
    });
    setShowModal(true);
  };

  const handlePreview = (template) => {
    // Keep all original styling but ensure content text is dark
    setPreviewHtml(template.body);
    setShowPreviewModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!await confirm.danger('Er du sikker på at du vil slette denne malen?')) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(`${API}/admin/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-postmal slettet!');
      fetchTemplates();
    } catch (error) {
      toast.error('Kunne ikke slette e-postmalen');
      console.error('Delete template error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = getToken();
      
      if (editingTemplate) {
        // Update existing template
        await axios.put(
          `${API}/admin/email-templates/${editingTemplate.id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('E-postmal oppdatert!');
      } else {
        // Create new template
        await axios.post(
          `${API}/admin/email-templates`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('E-postmal opprettet!');
      }
      
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error(`Kunne ikke lagre e-postmalen: ${error.response?.data?.detail || error.message}`);
      console.error('Save template error:', error);
    }
  };

  const getTemplateTypeLabel = (type) => {
    const found = templateTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="admin-header">
          <h1>{t("superAdmin.emailTemplates.title")}</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'white' }}>
          Laster...
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t("superAdmin.emailTemplates.title")}</h1>
        <button 
          className="btn btn-primary"
          onClick={handleCreate}
        >
          <Plus size={20} /> {t("superAdmin.emailTemplates.newTemplate")}
        </button>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('superAdmin.emailTemplates.type')}</th>
              <th>{t('superAdmin.emailTemplates.subject')}</th>
              <th>{t('common.create')}</th>
              <th style={{ textAlign: 'right' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              templates.map(template => (
                <tr key={template.id}>
                  <td>{template.name}</td>
                  <td>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      background: 'rgba(80, 227, 194, 0.2)',
                      color: '#50E3C2',
                      fontSize: '0.875rem'
                    }}>
                      {getTemplateTypeLabel(template.template_type)}
                    </span>
                  </td>
                  <td>{template.subject}</td>
                  <td>
                    {new Date(template.created_at).toLocaleDateString('no-NO', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-icon"
                        onClick={() => handlePreview(template)}
                        title="Forhåndsvis"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        className="btn btn-icon"
                        onClick={() => handleEdit(template)}
                        title="Rediger"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        className="btn btn-icon btn-danger"
                        onClick={() => handleDelete(template.id)}
                        title="Slett"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h2>{editingTemplate ? 'Rediger e-postmal' : 'Ny e-postmal'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Malnavn *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="F.eks. Velkomst-e-post for nye brukere"
                  />
                </div>

                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={formData.template_type}
                    onChange={(e) => setFormData({...formData, template_type: e.target.value})}
                    required
                    style={{
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      width: '100%'
                    }}
                  >
                    {templateTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>E-post emne *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    placeholder="F.eks. Velkommen til Infovegg.no!"
                  />
                </div>

                <div className="form-group">
                  <label>E-post innhold (HTML) *</label>
                  <WYSIWYGEditor
                    value={formData.body}
                    onChange={(html) => setFormData({...formData, body: html})}
                    placeholder="Skriv e-post innholdet her. Du kan bruke HTML-formatering."
                    minHeight="300px"
                  />
                  <small style={{ color: '#a0a0a0', marginTop: '0.5rem', display: 'block' }}>
                    Tips: Du kan bruke variabler som {`{{user_name}}`}, {`{{organization_name}}`}, {`{{reset_link}}`} osv.
                  </small>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={20} /> {editingTemplate ? 'Oppdater' : 'Opprett'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h2>Forhåndsvisning</h2>
              <button className="modal-close" onClick={() => setShowPreviewModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div 
                style={{
                  background: 'white',
                  color: '#2A2A2A',
                  padding: '2rem',
                  borderRadius: '8px',
                  minHeight: '400px'
                }}
                className="email-preview-content"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminEmailTemplates;
